import { LANGS, LANGUAGES, t, type Lang } from '../i18n';
import { counterKey } from './counter';
import FACTS from './welcomeFacts.json';
// Beta builds only (MAGIA_BETA=1; dropped from production with the code behind __BETA__).
import { markDialog } from '../beta/aids';
import { WELCOME_SCREENS } from '../beta/screenCodes';

/**
 * First-visit welcome: three short screens, reopenable from "How it works" in the footer. Screen 1 is
 * only the language: large buttons, each language in its own script (nothing to read first). Screen 2:
 * why and what. Screen 3: privacy and limits. A ✕ "Close" on every screen, Back/Next, Start at the end. A modal <dialog>: the page
 * behind it is inert, Tab cycles inside it, Esc closes it, focus goes to each screen's heading and
 * back where it was on closing. It lives outside the app's root (which is redrawn on every render).
 * "Don't show again" (ticked by default) is remembered per browser in localStorage; when storage is
 * blocked the welcome simply shows again next time.
 */
/** Welcome screen 2's facts and their sources (Matt, 2026-10-11; the links live in welcomeFacts.json). */
const WELCOME_FACTS: [string, string][] = FACTS.facts.map((f) => [f.key, f.url]);

export const WELCOME_KEY = 'magia-li.welcome';
export const WELCOME_STEPS = 3;

/** True unless this browser said "don't show again". */
export function shouldShowWelcome(storage: () => Pick<Storage, 'getItem'> = () => localStorage): boolean {
  try {
    return storage().getItem(WELCOME_KEY) !== 'dismissed';
  } catch {
    return true;
  }
}

/** `dontShowAgain`: what the user chose with the box (it starts unticked); undefined: never touched, storage stays as it is. */
export function rememberWelcome(dontShowAgain: boolean | undefined, storage: () => Pick<Storage, 'setItem' | 'removeItem'> = () => localStorage): void {
  try {
    if (dontShowAgain === undefined) return;
    if (dontShowAgain) storage().setItem(WELCOME_KEY, 'dismissed');
    else storage().removeItem(WELCOME_KEY);
  } catch {
    // Storage blocked (private mode etc.): not essential.
  }
}

export interface Welcome {
  open(): void;
  /** Redraws an open welcome (after a language change). */
  refresh(): void;
}

interface Deps {
  lang(): Lang;
  /** Switches the whole app's language (entered data is kept). */
  setLang(lang: Lang): void | Promise<void>;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function createWelcome(deps: Deps): Welcome {
  const dialog = document.createElement('dialog');
  dialog.className = 'welcome';
  dialog.setAttribute('aria-labelledby', 'welcome-title');
  dialog.setAttribute('aria-describedby', 'welcome-body');
  document.body.append(dialog);
  let step = 0;
  let dontShowAgain: boolean | undefined;
  let returnTo: Element | null = null;

  const el = <K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, string> = {}, ...children: (Node | string)[]) => {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
    node.append(...children);
    return node;
  };
  const button = (label: string, onClick: () => void, variant: 'primary' | 'secondary' = 'primary') => {
    const b = el('button', { type: 'button', class: variant }, label);
    b.addEventListener('click', onClick);
    return b;
  };

  const close = () => {
    rememberWelcome(dontShowAgain);
    if (dialog.open) dialog.close();
    // Right after closing (the browser's own focus restore runs inside close()): back where the user
    // was ("How it works"); on a first visit nothing was focused, so the first step, "Choose files".
    if (returnTo instanceof HTMLElement && returnTo !== document.body && returnTo.isConnected) returnTo.focus();
    else (document.querySelector<HTMLElement>('main h2[tabindex="-1"]') ?? document.getElementById('pdf-choose'))?.focus();
  };
  // Esc (the dialog's cancel) closes it like Skip.
  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });
  // Focus stays inside: Tab from the last control goes to the first, Shift+Tab from the first to the last.
  dialog.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const items = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((i) => i.offsetParent !== null || i === document.activeElement);
    const first = items[0];
    const last = items.at(-1);
    if (!first || !last) return;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !dialog.contains(active) || active === dialog.querySelector('#welcome-title'))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !dialog.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  });

  const draw = (focus: 'title' | string = 'title') => {
    const lang = deps.lang();
    const tr = (key: string, params?: Record<string, string | number>) => t(lang, key, params);
    const go = (n: number) => {
      step = n;
      draw();
    };
    const closeButton = el('button', { type: 'button', class: 'welcome-close', 'aria-label': tr('welcome.close') }, '✕');
    closeButton.addEventListener('click', close);
    const body: HTMLElement[] = [];
    let title: HTMLElement;
    if (step === 0) {
      // Language only: one visible word says what the screen is for; the current language first, two columns, one shared
      // note for the machine translations (ux-expert, start.md "welcome").
      title = el('h2', { id: 'welcome-title', tabindex: '-1' }, tr('app.language'));
      body.push(el('ul', { id: 'welcome-body', class: 'welcome-languages' },
        ...[lang, ...LANGS.filter((l) => l !== lang)].map((l) => {
          const b = el('button', { type: 'button', lang: l, dir: LANGUAGES[l].dir, id: `welcome-lang-${l}`, ...(l === lang ? { 'aria-current': 'true' } : {}) },
            el('span', { class: 'lang-name' }, LANGUAGES[l].nativeName));
          b.addEventListener('click', () => {
            // The language first (it may load), then on to screen 2 with the focus on its heading.
            // The language already in use is not set again (that redraws the page behind and loses the focus's way back).
            void Promise.resolve(l === lang ? undefined : deps.setLang(l)).then(() => {
              step = 1;
              draw();
            });
          });
          // A long name (Português (Brasil)) takes the whole row, never one word per line.
          return el('li', LANGUAGES[l].nativeName.length > 14 ? { class: 'wide' } : {}, b);
        })));
      body.push(el('p', { class: 'help welcome-beta' }, tr('welcome.betaNote')));
    } else if (step === 1) {
      title = el('h2', { id: 'welcome-title', tabindex: '-1' }, tr('welcome.why.title'));
      body.push(el('p', { id: 'welcome-body' }, tr('welcome.why.text')));
      // Two facts, each with its source (fixed links, opened in a new tab, never fetched).
      body.push(el('ul', { class: 'welcome-facts' }, ...WELCOME_FACTS.map(([key, url]) =>
        el('li', {}, tr(`welcome.why.${key}`), ' ', el('a', { href: url, target: '_blank', rel: 'noopener noreferrer' }, tr(`welcome.why.${key}Source`))))));
    } else {
      title = el('h2', { id: 'welcome-title', tabindex: '-1' }, tr('welcome.trust.title'));
      const disclaimer = el('a', { href: '#/disclaimer' }, tr('welcome.trust.disclaimer'));
      disclaimer.addEventListener('click', () => {
        // The page behind is inert while the dialog is open: close first, then follow the link.
        returnTo = null;
        close();
      });
      body.push(el('div', { id: 'welcome-body' },
        el('ul', {}, ...['device', 'minimums', 'notAdvice', 'worthChecking', 'beta'].map((k) => el('li', {}, tr(k === 'minimums' ? 'scope.law' : k === 'device' ? counterKey('welcome.trust.device') : `welcome.trust.${k}`)))),
        el('p', {}, disclaimer)));
    }
    const remember = el('input', { type: 'checkbox', id: 'welcome-remember' });
    remember.checked = dontShowAgain === true;
    remember.addEventListener('change', () => (dontShowAgain = remember.checked));
    const last = step === WELCOME_STEPS - 1;
    dialog.replaceChildren(
      el('div', { class: 'welcome-top' }, step > 0 ? el('p', { class: 'welcome-step' }, tr('welcome.step', { step: step + 1, steps: WELCOME_STEPS })) : el('span', {}), closeButton),
      title,
      ...body,
      step > 0 ? el('label', { class: 'choice welcome-remember' }, remember, tr('welcome.dontShowAgain')) : '',
      el('div', { class: 'row welcome-nav' },
        step > 0 ? button(tr('welcome.back'), () => go(step - 1), 'secondary') : '',
        // Screen 1: choosing a language goes on, so there is no "Next" (a second primary button).
        last ? button(tr('welcome.start'), close) : step === 0 ? '' : button(tr('welcome.next'), () => go(step + 1))),
    );
    // Beta builds: this screen's code and "Report a problem on this page" (src/beta), in the dialog's last row.
    if (__BETA__) markDialog(dialog, WELCOME_SCREENS[step] ?? 'welcomeTrust', lang, tr);
    (focus === 'title' ? dialog.querySelector<HTMLElement>(step === 0 ? `#welcome-lang-${lang}` : '#welcome-title') : document.getElementById(focus))?.focus();
  };

  return {
    open() {
      step = 0;
      dontShowAgain = undefined;
      returnTo = document.activeElement;
      draw('none');
      if (!dialog.open) dialog.showModal();
      // Screen 1: the current language's button, so Enter keeps it and arrows/Tab reach the others.
      dialog.querySelector<HTMLElement>(`#welcome-lang-${deps.lang()}`)?.focus();
    },
    refresh() {
      if (dialog.open) draw(step === 0 ? `welcome-lang-${deps.lang()}` : 'none');
    },
  };
}
